import React, { Suspense, lazy } from 'react';
import { CallbackLogin, PrivateOutlet } from '@applications-terrains/birdz-react-library';
import { Navigate, Outlet } from 'react-router-dom';
import { Box } from '@mui/material';
import DotsLoader from './components/Tools/DotsLoader/DotsLoader';
import EquipementConfiguration from './components/Extractor/Equipments/Configuration/EquipmentConfiguration';
import EquipmentFamilies from './components/Extractor/Equipments/Families/EquipmentFamilies';
import InterventionTypes from './components/Extractor/Equipments/InterventionTypes/InterventionTypes';
import { t } from 'i18next';
import InterventionCreationPage from './components/BConnect/Interventions/InterventionCreation/InterventionCreationPage';
import FollowUpLayout from './components/BConnect/FollowUp/FollowUpContext';
import ReferencesTables from './components/Extractor/Equipments/ReferencesTables/ReferencesTablesMenu';

// Le suivi charge Leaflet : chargé à la demande, hors du bundle principal.
const FollowUpPage = lazy(() => import('./components/BConnect/FollowUp/FollowUpPage'));
const FollowUpDetail = lazy(() => import('./components/BConnect/FollowUp/FollowUpDetail'));
const Home = lazy(() => import('./components/Home/Home'));
const Login = lazy(() => import('./components/Login/Login'));
const DashboardLayout = lazy(() => import('./components/DashboardLayout/DashboardLayout'));
const Diagnostics = lazy(() => import('./components/CR/Diagnostics/Diagnostics'));
const DMSManager = lazy(() => import('./components/DMS/DMSManager'));
const Users = lazy(() => import('./components/Admin/Users/Users'));
const Groups = lazy(() => import('./components/Admin/Groups/Groups'));
const Permissions = lazy(() => import('./components/Admin/Permissions/Permissions'));
const Logs = lazy(() => import('./components/Admin/Log/Logs'));
const Subcontractors = lazy(() => import('./components/Admin/Subcontractor/Subcontractor'));
const Contracts = lazy(() => import('./components/Admin/Contract/Contracts'));
const Messages = lazy(() => import('./components/Admin/Messages/Messages'));
const CrCases = lazy(() => import('./components/CR/Cases/CrCases'));
const CrCaseForm = lazy(() => import('./components/CR/Cases/CrCaseForm'));
const NotFound = lazy(() => import('./components/NotFound/NotFound'));
const SamplePage = lazy(() => import('./components/SamplePage'));
const ParamsList = lazy(() => import('./components/Admin/Params/ParamsList'));
const SharedPerimeters = lazy(() => import('./components/Admin/SharedPerimeters/SharedPerimeters'));
const Cities = lazy(() => import('./components/Admin/Cities/Cities'));
const SharedPerimeterForm = lazy(
  () => import('./components/Admin/SharedPerimeters/SharedPerimeterForm')
);
const Planning = lazy(() => import('./components/Planning/Planning'));
const Informations = lazy(() => import('./components/Informations/Informations'));
const MonthlyStatementListing = lazy(
  () => import('./components/MonthlyStatement/MonthlyStatementListing')
);
const SubcontractorBOE = lazy(() => import('./components/Admin/Subcontractor/SubcontractorBOE'));
const CallsListing = lazy(() => import('./components/Calls/CallsListing'));
const SearchCallsForm = lazy(() => import('./components/Calls/SearchCallsForm'));
const InformationDetails = lazy(() => import('./components/Informations/InformationDetails'));
const SearchDevices = lazy(() => import('./components/CR/SearchDevices/SearchDevices'));
const InterventionForm = lazy(() => import('./components/CR/Interventions/InterventionForm'));
const Interventions = lazy(() => import('./components/CR/Interventions/Interventions'));
const MonthlyStatementDetails = lazy(
  () => import('./components/MonthlyStatement/MonthlyStatementDetails')
);
const SubcontractorMonthlyStatementDetails = lazy(
  () => import('./components/MonthlyStatement/SubcontractorMonthlyStatementDetails')
);
const MrCases = lazy(() => import('./components/MR/Cases/MrCases'));
const MrCaseForm = lazy(() => import('./components/MR/Cases/MrCaseForm'));
const MrCaseDiagnostic = lazy(() => import('./components/MR/Cases/MrCaseDiagnostic'));
const MrCaseInterventionDetails = lazy(
  () => import('./components/MR/Cases/MrCaseInterventionDetails')
);
const CrStatistics = lazy(() => import('./components/CR/Statistics/CrStatistics'));
const PasswordReset = lazy(() => import('./components/PasswordReset/PasswordReset'));
const HomePage = lazy(() => import('./components/BConnect/HomePage'));
const Clients = lazy(() => import('./components/Extractor/Clients/Clients'));
const ClientForm = lazy(() => import('./components/Extractor/Clients/ClientsForm'));
const Erps = lazy(() => import('./components/Extractor/Erps/Erps'));
const ErpForm = lazy(() => import('./components/Extractor/Erps/ErpForm'));
const AnomalyProfiles = lazy(
  () => import('./components/Extractor/AnomalyProfiles/AnomalyProfiles')
);
const ExportFormats = lazy(() => import('./components/Extractor/ExportFormats/ExportFormats'));
const ExportFormatForm = lazy(
  () => import('./components/Extractor/ExportFormats/ExportFormatForm')
);
const Dashboard = lazy(() => import('./components/BConnect/Dashboard/Dashboard'));
const ExportFormatSettings = lazy(
  () => import('./components/Extractor/ExportFormats/ExportFormatSettings')
);
const LicencesList = lazy(() => import('./components/BConnect/Dashboard/LicencesList'));
const Jobs = lazy(() => import('./components/Extractor/Jobs/Jobs'));
const JobForm = lazy(() => import('./components/Extractor/Jobs/JobForm'));
const JobsExecutions = lazy(() => import('./components/Extractor/Jobs/JobsExecutions'));
const NotationsList = lazy(() => import('./components/BConnect/Dashboard/NotationsList'));
const BconnectUsers = lazy(() => import('./components/BConnect/BconnectUsers'));
const CorrespondanceDetails = lazy(
  () => import('./components/Extractor/Correspondances/details/CorrespondanceDetails')
);
const ExportDetail = lazy(() => import('./components/Extractor/Jobs/ExportDetails'));
const ExtractorInterventions = lazy(
  () => import('./components/Extractor/Interventions/Interventions')
);
const ExtractorInterventionForm = lazy(
  () => import('./components/Extractor/Interventions/InterventionForm')
);
const CorrespondancesList = lazy(
  () => import('./components/Extractor/Correspondances/list/CorrespondancesList')
);
const SessionsList = lazy(() => import('./components/BStock/Sessions/SessionsList'));
const ConfigurationsList = lazy(
  () => import('./components/BStock/Configurations/ConfigurationsList')
);
const ConfigurationDetail = lazy(
  () => import('./components/BStock/Configurations/ConfigurationDetail')
);
const SessionDetail = lazy(() => import('./components/BStock/Sessions/SessionDetail'));
const InterventionDetails = lazy(
  () => import('./components/BConnect/Interventions/InterventionDetails')
);
const ToursList = lazy(() => import('./components/BReader/BOI/Tours/ToursList'));
const TourDetails = lazy(
  () => import('./components/BReader/BOE/Tours/TourForm/TourDetails/TourDetails')
);
const ExtractorInterventionsWrapper = lazy(
  () => import('./components/Extractor/Interventions/ExtractorInterventionsWrapper')
);
const Tours = lazy(() => import('./components/BReader/Tours'));
const RouteRedirect = lazy(() => import('./components/Tools/RouteRedirect'));
const LegacyToursRedirect = lazy(() => import('./components/BReader/LegacyToursRedirect'));
const TourForm = lazy(() => import('./components/BReader/TourForm'));
const Project = lazy(() => import('./components/CR/Deployment/Project'));
const ProjectDetail = lazy(() => import('./components/CR/Deployment/ProjectDetail'));
const ReferencesHubPage = lazy(() => import('./components/References/Hub/HubPage'));
const StepsPage = lazy(() => import('./components/References/Steps/StepsPage'));
const StepsToSkipPage = lazy(() => import('./components/References/StepsToSkip/StepsToSkipPage'));
const CustomersPage = lazy(() => import('./components/References/Customers/CustomersPage'));
const CustomerPage = lazy(() => import('./components/References/Customers/CustomerPage'));
const FieldListsPage = lazy(() => import('./components/References/FieldLists/FieldListsPage'));
const MaintenanceTypesPage = lazy(
  () => import('./components/References/MaintenanceTypes/MaintenanceTypesPage')
);

export const fallback = (
  <Box
    className={`w-100 h-100 d-flex align-items-center justify-content-center longer-transition`}
    sx={{
      minHeight: 'calc(100vh - 350px)',
      boxSizing: 'border-box',
      fontSize: '1.2rem'
    }}
  >
    <DotsLoader message={t('loading')} />
  </Box>
);

export type RouteProps = {
  path: string;
  element?: any;
  label?: string;
  breadcrumb?: string;
  permissions?: string[];
  children?: RouteProps[];
};

const routes: RouteProps[] = [
  {
    path: '/callback',
    element: (
      <Suspense fallback={fallback}>
        <CallbackLogin />
      </Suspense>
    )
  },
  {
    path: '/auth',
    element: (
      <Suspense fallback={fallback}>
        <Login />
      </Suspense>
    )
  },
  {
    path: '/password-reset',
    element: (
      <Suspense fallback={fallback}>
        <PasswordReset />
      </Suspense>
    )
  },
  {
    path: '/',
    element: (
      <Suspense fallback={fallback}>
        <DashboardLayout component={<PrivateOutlet />} />
      </Suspense>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense fallback={fallback}>
            <Home />
          </Suspense>
        ),
        breadcrumb: 'routes.home',
        label: 'Home'
      },
      {
        path: 'bconnect',
        label: 'routes.bconnect',
        element: (
          <Suspense fallback={fallback}>
            <RouteRedirect baseRoute="/bconnect" targetRoute="/bconnect/homepage" />
          </Suspense>
        ),
        permissions: ['BCONNECT_MENU'],
        children: [
          {
            path: 'homepage',
            element: (
              <Suspense fallback={fallback}>
                <HomePage />
              </Suspense>
            )
          },
          {
            path: 'new-intervention',
            label: 'sidebar.newIntervention',
            element: (
              <Suspense fallback={fallback}>
                <InterventionCreationPage />
              </Suspense>
            )
          },
          {
            path: 'dashboard',
            label: 'sidebar.dashboard',
            element: (
              <Suspense fallback={fallback}>
                <RouteRedirect
                  baseRoute="/bconnect/dashboard"
                  targetRoute="/bconnect/dashboard/overview"
                />
              </Suspense>
            ),
            children: [
              {
                path: 'overview',
                label: 'sidebar.overview',
                element: (
                  <Suspense fallback={fallback}>
                    <Dashboard />
                  </Suspense>
                )
              },
              {
                path: 'licences',
                label: 'sidebar.licenses',
                element: (
                  <Suspense fallback={fallback}>
                    <LicencesList />
                  </Suspense>
                )
              },
              {
                path: 'notations',
                label: 'sidebar.notations',
                element: (
                  <Suspense fallback={fallback}>
                    <NotationsList />
                  </Suspense>
                )
              }
            ]
          },
          {
            path: 'users',
            label: 'Users',
            children: [
              {
                path: '',
                label: 'sidebar.users',
                element: (
                  <Suspense fallback={fallback}>
                    <BconnectUsers action="list" />
                  </Suspense>
                )
              },
              {
                path: 'edit/:id',
                label: 'edit',
                element: (
                  <Suspense fallback={fallback}>
                    <BconnectUsers action="edit" />
                  </Suspense>
                )
              }
            ]
          },
          {
            path: 'follow-up',
            label: 'followUp.title',
            permissions: ['BCONNECT_FOLLOW_UP'],
            element: <FollowUpLayout />,
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={fallback}>
                    <FollowUpPage />
                  </Suspense>
                )
              },
              {
                path: ':id',
                label: 'details',
                element: (
                  <Suspense fallback={fallback}>
                    <FollowUpDetail />
                  </Suspense>
                )
              }
            ]
          },
          {
            path: 'interventions',
            label: 'sidebar.monitoring',
            element: (
              <Suspense fallback={fallback}>
                <ExtractorInterventionsWrapper />
              </Suspense>
            ),
            children: [
              {
                path: 'pending',
                element: (
                  <Suspense fallback={fallback}>
                    <ExtractorInterventions target={'pending'} />
                  </Suspense>
                ),
                label: 'Interventions'
              },
              {
                path: 'pending/details/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <InterventionDetails />
                  </Suspense>
                ),
                label: 'details'
              }
            ]
          },
          // {
          //   path: 'extractor',
          //   label: 'extract',
          //   element: (
          //     <Suspense fallback={fallback}>
          //       <RouteRedirect
          //         baseRoute="/extractor"
          //         targetRoute="/extractor/interventions"
          //       />
          //     </Suspense>
          //   ),
          //   children: [
          //     {
          //       path: 'interventions',
          //       label: 'Interventions',
          //       element: (
          //         <Suspense fallback={fallback}>
          //           <ExtractorInterventionsWrapper />
          //         </Suspense>
          //       ),
          //       children: [
          //         {
          //           path: '',
          //           element: (
          //             <Suspense fallback={fallback}>
          //               <ExtractorInterventions target={'all'} />
          //             </Suspense>
          //           ),
          //           label: 'sidebar.completedInterventions'
          //         },
          //         {
          //           path: 'edit/:id',
          //           element: (
          //             <Suspense fallback={fallback}>
          //               <ExtractorInterventionForm readOnly={false} />
          //             </Suspense>
          //           ),
          //           label: 'edit'
          //         },
          //         {
          //           path: 'view/:id',
          //           element: (
          //             <Suspense fallback={fallback}>
          //               <ExtractorInterventionForm readOnly={true} />
          //             </Suspense>
          //           ),
          //           label: 'details'
          //         }
          //       ]
          //     },
          //     {
          //       path: 'jobs',
          //       label: 'Jobs',
          //       children: [
          //         {
          //           path: '',
          //           element: (
          //             <Suspense fallback={fallback}>
          //               <Jobs />
          //             </Suspense>
          //           ),
          //           label: 'routes.jobs.list'
          //         },
          //         {
          //           path: 'add',
          //           element: (
          //             <Suspense fallback={fallback}>
          //               <JobForm />
          //             </Suspense>
          //           ),
          //           label: 'routes.jobs.add'
          //         },
          //         {
          //           path: 'edit/:id',
          //           element: (
          //             <Suspense fallback={fallback}>
          //               <JobForm />
          //             </Suspense>
          //           ),
          //           label: 'routes.jobs.edit'
          //         }
          //       ]
          //     },
          //     {
          //       path: 'executions',
          //       label: 'Historique des exports',
          //       children: [
          //         {
          //           path: '',
          //           element: (
          //             <Suspense fallback={fallback}>
          //               <JobsExecutions />
          //             </Suspense>
          //           ),
          //           label: 'routes.exports.history'
          //         },
          //         {
          //           path: 'details/:id',
          //           element: (
          //             <Suspense fallback={fallback}>
          //               <ExportDetail />
          //             </Suspense>
          //           ),
          //           label: 'details'
          //         }
          //       ]
          //     }
          //   ]
          // },
          {
            path: 'families',
            element: (
              <Suspense fallback={fallback}>
                <EquipmentFamilies />
              </Suspense>
            ),
            label: 'sidebar.families'
          },
          {
            path: 'intervention_types',
            element: (
              <Suspense fallback={fallback}>
                <InterventionTypes />
              </Suspense>
            ),
            label: 'sidebar.interventionTypes'
          },
          {
            path: 'references_tables',
            element: (
              <Suspense fallback={fallback}>
                <ReferencesTables />
              </Suspense>
            ),
            label: 'sidebar.referencesTables'
          }
        ]
      },
      //
      {
        path: 'extractor',
        label: 'extract',
        permissions: ['EXTRACTOR_MENU'],
        element: (
          <Suspense fallback={fallback}>
            <RouteRedirect baseRoute="/extractor" targetRoute="/extractor/interventions" />
          </Suspense>
        ),
        children: [
          {
            path: 'interventions',
            label: 'Interventions',
            element: (
              <Suspense fallback={fallback}>
                <ExtractorInterventionsWrapper />
              </Suspense>
            ),
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={fallback}>
                    <ExtractorInterventions target={'all'} />
                  </Suspense>
                ),
                label: 'sidebar.completedInterventions'
              },
              {
                path: 'edit/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <ExtractorInterventionForm readOnly={false} />
                  </Suspense>
                ),
                label: 'edit'
              },
              {
                path: 'view/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <ExtractorInterventionForm readOnly={true} />
                  </Suspense>
                ),
                label: 'details'
              }
            ]
          },
          {
            path: 'jobs',
            label: 'Jobs',
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={fallback}>
                    <Jobs />
                  </Suspense>
                ),
                label: 'routes.jobs.list'
              },
              {
                path: 'add',
                element: (
                  <Suspense fallback={fallback}>
                    <JobForm />
                  </Suspense>
                ),
                label: 'routes.jobs.add'
              },
              {
                path: 'edit/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <JobForm />
                  </Suspense>
                ),
                label: 'routes.jobs.edit'
              }
            ]
          },
          {
            path: 'executions',
            label: 'Historique des exports',
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={fallback}>
                    <JobsExecutions />
                  </Suspense>
                ),
                label: 'routes.exports.history'
              },
              {
                path: 'details/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <ExportDetail />
                  </Suspense>
                ),
                label: 'details'
              }
            ]
          },
          {
            path: 'admin',
            label: 'administration',
            element: (
              <Suspense fallback={fallback}>
                <RouteRedirect
                  baseRoute="/extractor/admin"
                  targetRoute="/extractor/admin/clients"
                />
              </Suspense>
            ),
            children: [
              {
                path: 'clients',
                children: [
                  {
                    path: '',
                    element: (
                      <Suspense fallback={fallback}>
                        <Clients />
                      </Suspense>
                    ),
                    label: 'sidebar.clients'
                  },
                  {
                    path: 'add',
                    element: (
                      <Suspense fallback={fallback}>
                        <ClientForm />
                      </Suspense>
                    ),
                    label: 'add'
                  },
                  {
                    path: 'edit/:id',
                    element: (
                      <Suspense fallback={fallback}>
                        <ClientForm />
                      </Suspense>
                    ),
                    label: 'edit'
                  }
                ]
              },
              {
                path: 'erps',
                children: [
                  {
                    path: '',
                    element: (
                      <Suspense fallback={fallback}>
                        <Erps />
                      </Suspense>
                    ),
                    label: 'ERPs'
                  },
                  {
                    path: 'add',
                    element: (
                      <Suspense fallback={fallback}>
                        <ErpForm />
                      </Suspense>
                    ),
                    label: 'add'
                  },
                  {
                    path: 'edit/:id',
                    element: (
                      <Suspense fallback={fallback}>
                        <ErpForm />
                      </Suspense>
                    ),
                    label: 'edit'
                  }
                ]
              },
              {
                path: 'anomaly-profiles',
                children: [
                  {
                    path: '',
                    element: (
                      <Suspense fallback={fallback}>
                        <AnomalyProfiles action="list" />
                      </Suspense>
                    ),
                    label: 'sidebar.anomalyProfiles'
                  },
                  {
                    path: 'add',
                    element: (
                      <Suspense fallback={fallback}>
                        <AnomalyProfiles action="add" />
                      </Suspense>
                    ),
                    label: 'add'
                  },
                  {
                    path: 'edit/:id',
                    element: (
                      <Suspense fallback={fallback}>
                        <AnomalyProfiles action="edit" />
                      </Suspense>
                    ),
                    label: 'edit'
                  }
                ]
              },
              {
                path: 'export-formats',
                children: [
                  {
                    path: '',
                    element: <Navigate to="bconnect" replace />,
                    label: 'sidebar.exportFormats'
                  },
                  {
                    path: 'bconnect',
                    element: (
                      <Suspense fallback={fallback}>
                        <ExportFormats app="BCONNECT" />
                      </Suspense>
                    ),
                    label: 'sidebar.bConnect'
                  },
                  {
                    path: 'bconnect/add',
                    element: (
                      <Suspense fallback={fallback}>
                        <ExportFormatForm app="BCONNECT" />
                      </Suspense>
                    ),
                    label: 'add'
                  },
                  {
                    path: 'bstock',
                    element: (
                      <Suspense fallback={fallback}>
                        <ExportFormats app="BSTOCK" />
                      </Suspense>
                    ),
                    label: 'sidebar.bStock'
                  },
                  {
                    path: 'bstock/add',
                    element: (
                      <Suspense fallback={fallback}>
                        <ExportFormatForm app="BSTOCK" />
                      </Suspense>
                    ),
                    label: 'add'
                  },
                  {
                    path: 'bconnect/edit/:id',
                    element: (
                      <Suspense fallback={fallback}>
                        <ExportFormatForm app="BCONNECT" />
                      </Suspense>
                    ),
                    label: 'edit'
                  },
                  {
                    path: 'bstock/edit/:id',
                    element: (
                      <Suspense fallback={fallback}>
                        <ExportFormatForm app="BSTOCK" />
                      </Suspense>
                    ),
                    label: 'edit'
                  },
                  {
                    path: 'bconnect/settings/:id',
                    element: (
                      <Suspense fallback={fallback}>
                        <ExportFormatSettings />
                      </Suspense>
                    ),
                    label: 'Paramètres'
                  },
                  {
                    path: 'bstock/settings/:id',
                    element: (
                      <Suspense fallback={fallback}>
                        <ExportFormatSettings />
                      </Suspense>
                    ),
                    label: 'Paramètres'
                  },
                  // Anciennes URL, sans application : la fiche ERP et la popup d'export y renvoient
                  // faute de la connaître. Le formulaire redirige alors vers l'URL de l'application.
                  {
                    path: 'edit/:id',
                    element: (
                      <Suspense fallback={fallback}>
                        <ExportFormatForm />
                      </Suspense>
                    ),
                    label: 'edit'
                  },
                  {
                    path: 'settings/:id',
                    element: (
                      <Suspense fallback={fallback}>
                        <ExportFormatSettings />
                      </Suspense>
                    ),
                    label: 'Paramètres'
                  }
                ]
              },
              {
                path: 'correspondances',
                children: [
                  {
                    path: '',
                    element: (
                      <Suspense fallback={fallback}>
                        <CorrespondancesList />
                      </Suspense>
                    ),
                    label: 'sidebar.correspondences'
                  },
                  {
                    path: 'details/:id',
                    element: (
                      <Suspense fallback={fallback}>
                        <CorrespondanceDetails />
                      </Suspense>
                    ),
                    label: 'details'
                  }
                ]
              }
            ]
          }
        ]
      },
      //
      {
        path: 'bstock',
        label: 'sidebar.bStock',
        permissions: ['BSTOCK_MENU'],
        element: (
          <Suspense fallback={fallback}>
            <RouteRedirect baseRoute="/bstock" targetRoute="/bstock/sessions" />
          </Suspense>
        ),
        children: [
          {
            path: 'sessions',
            children: [
              {
                path: '',
                label: 'bStock.sessions',
                element: (
                  <Suspense fallback={fallback}>
                    <SessionsList />
                  </Suspense>
                )
              },
              {
                path: 'details/:id',
                label: 'details',
                element: (
                  <Suspense fallback={fallback}>
                    <SessionDetail />
                  </Suspense>
                )
              }
            ]
          },
          {
            path: 'configurations',
            children: [
              {
                path: '',
                label: 'bStock.configurations',
                element: (
                  <Suspense fallback={fallback}>
                    <ConfigurationsList />
                  </Suspense>
                )
              },
              {
                path: 'add',
                label: 'add',
                element: (
                  <Suspense fallback={fallback}>
                    <ConfigurationDetail />
                  </Suspense>
                )
              },
              {
                path: 'edit/:id',
                label: 'details',
                element: (
                  <Suspense fallback={fallback}>
                    <ConfigurationDetail />
                  </Suspense>
                )
              }
            ]
          }
        ]
      },
      {
        path: 'administration',
        label: 'administration',
        element: (
          <Suspense fallback={fallback}>
            <RouteRedirect baseRoute="/administration" targetRoute="/administration/users" />
          </Suspense>
        ),
        permissions: ['ADMIN_MENU'],
        children: [
          {
            path: 'users',
            permissions: ['ADMIN_MENU_USER'],
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={fallback}>
                    <Users action="list" />
                  </Suspense>
                ),
                label: 'sidebar.users'
              },
              {
                path: 'create',
                element: (
                  <Suspense fallback={fallback}>
                    <Users action="add" />
                  </Suspense>
                ),
                label: 'add'
              },
              {
                path: 'edit/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <Users action="edit" />
                  </Suspense>
                ),
                label: 'edit'
              }
            ]
          },
          {
            path: 'groups',
            permissions: ['ADMIN_MENU_GROUP'],
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={fallback}>
                    <Groups action="list" />
                  </Suspense>
                ),
                label: 'sidebar.groups'
              },
              {
                path: 'add',
                element: (
                  <Suspense fallback={fallback}>
                    <Groups action="add" />
                  </Suspense>
                ),
                label: 'add'
              },
              {
                path: 'edit/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <Groups action="edit" />
                  </Suspense>
                ),
                label: 'edit'
              }
            ]
          },
          {
            path: 'permissions',
            label: 'sidebar.permissions',
            permissions: ['ADMIN_MENU_SECTION'],
            children: [
              {
                path: 'add',
                element: (
                  <Suspense fallback={fallback}>
                    <Permissions action="add" />
                  </Suspense>
                ),
                label: 'add'
              },
              {
                path: 'edit/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <Permissions action="edit" />
                  </Suspense>
                ),
                label: 'edit'
              },
              {
                path: '',
                element: (
                  <Suspense fallback={fallback}>
                    <Permissions action="list" />
                  </Suspense>
                ),
                label: 'sidebar.permissions'
              }
            ]
          },
          {
            path: 'subcontractors',
            label: 'Sous-traitants',
            permissions: ['ADMIN_MENU_ST'],
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={fallback}>
                    <Subcontractors action="list" />
                  </Suspense>
                ),
                label: 'sidebar.subcontractors'
              },
              {
                path: 'create',
                element: (
                  <Suspense fallback={fallback}>
                    <Subcontractors action="add" />
                  </Suspense>
                ),
                label: 'add'
              },
              {
                path: 'edit/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <Subcontractors action="edit" />
                  </Suspense>
                ),
                label: 'edit'
              }
            ]
          },
          {
            path: 'messages',
            permissions: ['ADMIN_MESSAGE'],
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={fallback}>
                    <Messages action="list" />
                  </Suspense>
                ),
                label: 'sidebar.messages'
              },
              {
                path: 'add',
                element: (
                  <Suspense fallback={fallback}>
                    <Messages action="add" />
                  </Suspense>
                ),
                label: 'add'
              },
              {
                path: 'edit/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <Messages action="edit" />
                  </Suspense>
                ),
                label: 'edit'
              }
            ]
          },
          {
            path: 'contracts',
            element: (
              <Suspense fallback={fallback}>
                <Contracts action="list" />
              </Suspense>
            ),
            label: 'sidebar.contracts'
          },
          {
            path: 'logs',
            element: (
              <Suspense fallback={fallback}>
                <Logs action="list" />
              </Suspense>
            ),
            permissions: ['ADMIN_LOG'],
            label: 'sidebar.logs'
          },
          {
            path: 'radio-configurations',
            element: (
              <Suspense fallback={fallback}>
                <EquipementConfiguration />
              </Suspense>
            ),
            permissions: ['ADMIN_CONFIG_RADIO'],
            label: 'sidebar.radioConfigurations'
          },
          {
            path: 'shared-perimeters',
            permissions: ['ADMIN_MENU_SHARED_PERIMETERS'],
            label: 'sidebar.sharedPerimeters',
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={fallback}>
                    <SharedPerimeters />
                  </Suspense>
                ),
                label: 'sidebar.sharedPerimeters'
              },
              {
                path: 'add',
                element: (
                  <Suspense fallback={fallback}>
                    <SharedPerimeterForm action="add" />
                  </Suspense>
                ),
                label: 'sharedPerimeters.addTitle'
              },
              {
                path: 'edit/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <SharedPerimeterForm action="edit" />
                  </Suspense>
                ),
                label: 'sharedPerimeters.editTitle'
              },
              {
                path: 'duplicate/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <SharedPerimeterForm action="duplicate" />
                  </Suspense>
                ),
                label: 'sharedPerimeters.duplicateTitle'
              }
            ]
          },
          {
            path: 'cities',
            label: 'sidebar.cities',
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={fallback}>
                    <Cities />
                  </Suspense>
                ),
                label: 'sidebar.cities'
              }
            ]
          },
          {
            path: 'tables',
            label: 'sidebar.techTables',
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={fallback}>
                    <ParamsList />
                  </Suspense>
                ),
                label: 'sidebar.techTables'
              },
              {
                path: 'details/:param',
                element: (
                  <Suspense fallback={fallback}>
                    <ParamsList />
                  </Suspense>
                ),
                label: 'parameter'
              }
            ]
          },
          {
            path: 'references',
            label: 'sidebar.references',
            // La permission est aussi posée sur chaque enfant : sans elle, une route fille
            // resterait atteignable par URL directe (cf. displayRoutes dans App.tsx).
            permissions: ['ADMIN_MENU_REFERENCES'],
            // Le parent ne fait que porter ses enfants : sans `Outlet`, le hub resterait
            // affiché par-dessus la route fille.
            element: (
              <Suspense fallback={fallback}>
                <Outlet />
              </Suspense>
            ),
            children: [
              {
                // Le hub distribue vers les référentiels : plus de redirection vers l'un
                // d'eux, qui devenait arbitraire dès le troisième.
                path: '',
                element: (
                  <Suspense fallback={fallback}>
                    <ReferencesHubPage />
                  </Suspense>
                ),
                permissions: ['ADMIN_MENU_REFERENCES'],
                label: 'references.hub.title'
              },
              {
                path: 'steps',
                element: (
                  <Suspense fallback={fallback}>
                    <StepsPage />
                  </Suspense>
                ),
                permissions: ['ADMIN_MENU_REFERENCES'],
                label: 'references.steps.title'
              },
              {
                path: 'steps-to-skip',
                element: (
                  <Suspense fallback={fallback}>
                    <StepsToSkipPage />
                  </Suspense>
                ),
                permissions: ['ADMIN_MENU_REFERENCES'],
                label: 'references.stepsToSkip.title'
              },
              {
                path: 'maintenance-types',
                element: (
                  <Suspense fallback={fallback}>
                    <MaintenanceTypesPage />
                  </Suspense>
                ),
                permissions: ['ADMIN_MENU_REFERENCES'],
                label: 'references.maintenanceTypes.title'
              },
              {
                // Sans liste dans l'URL, la première s'affiche.
                path: 'field-lists',
                element: (
                  <Suspense fallback={fallback}>
                    <FieldListsPage />
                  </Suspense>
                ),
                permissions: ['ADMIN_MENU_REFERENCES'],
                label: 'references.fieldLists.title'
              },
              {
                // Deux routes plutôt qu'un paramètre optionnel : le fil d'Ariane associait la route
                // à `/field-lists` et à `/field-lists/<liste>`, d'où « Listes terrain » en double.
                // Sans libellé, il s'arrête à « Listes terrain ».
                path: 'field-lists/:list',
                element: (
                  <Suspense fallback={fallback}>
                    <FieldListsPage />
                  </Suspense>
                ),
                permissions: ['ADMIN_MENU_REFERENCES']
              },
              {
                path: 'customers',
                element: (
                  <Suspense fallback={fallback}>
                    <CustomersPage />
                  </Suspense>
                ),
                permissions: ['ADMIN_MENU_REFERENCES'],
                label: 'references.customers.title'
              },
              {
                path: 'customers/:customer',
                element: (
                  <Suspense fallback={fallback}>
                    <CustomerPage />
                  </Suspense>
                ),
                // Pas de libellé : le fil d'Ariane s'arrête à « Clients », le nom du client est
                // déjà le titre de la page (sinon : « Clients › Clients »).
                permissions: ['ADMIN_MENU_REFERENCES']
              }
            ]
          }
        ]
      },
      {
        path: 'boi',
        permissions: ['BOI'],
        element: (
          <Suspense fallback={fallback}>
            <RouteRedirect baseRoute="/boi" targetRoute="/boi/cr/diagnostics" />
          </Suspense>
        ),
        children: [
          {
            path: 'calls',
            label: 'sidebar.calls',
            permissions: ['CALLS_MENU'],
            element: (
              <Suspense fallback={fallback}>
                <RouteRedirect baseRoute="/boi/calls" targetRoute="/boi/cr/diagnostics" />
              </Suspense>
            ),
            children: [
              {
                path: 'new',
                label: 'sidebar.reception',
                element: (
                  <Suspense fallback={fallback}>
                    <SearchCallsForm />
                  </Suspense>
                )
              },
              {
                path: 'listing',
                label: 'sidebar.listing',
                permissions: ['CALLS_MENU_PAGE'],
                element: (
                  <Suspense fallback={fallback}>
                    <CallsListing />
                  </Suspense>
                )
              }
            ]
          },
          {
            path: 'cr',
            label: 'sidebar.cr',
            permissions: ['CR_MENU'],
            element: (
              <Suspense fallback={fallback}>
                <RouteRedirect baseRoute="/boi/cr" targetRoute="/boi/cr/diagnostics" />
              </Suspense>
            ),
            children: [
              {
                path: 'diagnostics',
                permissions: ['CR_MENU_DIAG'],
                children: [
                  {
                    path: '',
                    element: (
                      <Suspense fallback={fallback}>
                        <SearchDevices />
                      </Suspense>
                    ),
                    label: 'Diagnostics'
                  },
                  {
                    path: 'details/:moduleAddress',
                    element: (
                      <Suspense fallback={fallback}>
                        <Diagnostics />
                      </Suspense>
                    ),
                    label: 'Details'
                  }
                ]
              },
              {
                path: 'cases',
                label: 'sidebar.cases',
                permissions: ['CR_MENU_DOSSIER'],
                children: [
                  {
                    path: '',
                    element: (
                      <Suspense fallback={fallback}>
                        <CrCases />
                      </Suspense>
                    ),
                    label: 'sidebar.cases'
                  },
                  {
                    path: 'edit/:id',
                    element: (
                      <Suspense fallback={fallback}>
                        <CrCaseForm />
                      </Suspense>
                    ),
                    label: 'details'
                  }
                ]
              },
              {
                path: 'interventions',
                label: 'sidebar.interventions',
                permissions: ['CR_MENU_INTER'],
                children: [
                  {
                    path: '',
                    element: (
                      <Suspense fallback={fallback}>
                        <Interventions />
                      </Suspense>
                    ),
                    label: 'sidebar.interventions'
                  },
                  {
                    path: 'edit/:id',
                    element: (
                      <Suspense fallback={fallback}>
                        <InterventionForm />
                      </Suspense>
                    ),
                    label: 'details'
                  }
                ]
              },
              {
                path: 'statistics',
                label: 'sidebar.crpDashboard',
                permissions: ['CR_MENU_TAB_CRP'],
                element: (
                  <Suspense fallback={fallback}>
                    <RouteRedirect
                      baseRoute="/boi/cr/statistics"
                      targetRoute="/boi/cr/statistics/cr+"
                    />
                  </Suspense>
                ),
                children: [
                  {
                    path: 'cr+',
                    element: (
                      <Suspense fallback={fallback}>
                        <CrStatistics />
                      </Suspense>
                    ),
                    label: 'cR+MaintenanceDashboard'
                  }
                ]
              }
            ]
          },
          {
            path: 'project',
            label: 'deploymentProjectCr.menuTitle',
            permissions: ['CR_MENU_TAB_CRP'],
            children: [
              {
                path: '',
                label: 'deploymentProjectCr.menuTitle',
                element: (
                  <Suspense fallback={fallback}>
                    <Project />
                  </Suspense>
                )
              },
              {
                path: ':id',
                label: 'deploymentProjectCr.projectSheet',
                element: (
                  <Suspense fallback={fallback}>
                    <ProjectDetail />
                  </Suspense>
                )
              }
            ]
          },
          {
            path: 'mr',
            label: 'sidebar.mr',
            permissions: ['MR_MENU'],
            element: (
              <Suspense fallback={fallback}>
                <RouteRedirect baseRoute="/boi/mr" targetRoute="/boi/mr/diagnostics" />
              </Suspense>
            ),
            children: [
              {
                path: 'cases',
                label: 'sidebar.cases',
                permissions: ['MR_MENU_DOSSIER'],
                children: [
                  {
                    path: '',
                    element: (
                      <Suspense fallback={fallback}>
                        <MrCases />
                      </Suspense>
                    ),
                    label: 'sidebar.cases'
                  },
                  {
                    path: 'edit/:id',
                    element: (
                      <Suspense fallback={fallback}>
                        <MrCaseForm />
                      </Suspense>
                    ),
                    label: 'details'
                  },
                  {
                    path: ':caseId/intervention/:id',
                    label: 'Intervention details',
                    element: (
                      <Suspense fallback={fallback}>
                        <MrCaseInterventionDetails />
                      </Suspense>
                    )
                  }
                ]
              },
              {
                path: 'diagnostics',
                element: (
                  <Suspense fallback={fallback}>
                    <MrCaseDiagnostic />
                  </Suspense>
                ),
                label: 'Diagnostics'
              }
            ]
          },
          {
            path: 'dms',
            permissions: ['GED_MENU'],
            label: 'sidebar.dms',
            children: [
              {
                path: '',
                label: 'sidebar.dms',
                element: (
                  <Suspense fallback={fallback}>
                    <DMSManager />
                  </Suspense>
                )
              },
              {
                path: ':driveName/:driveId',
                element: (
                  <Suspense fallback={fallback}>
                    <DMSManager />
                  </Suspense>
                )
              }
            ]
          },
          {
            path: 'informations',
            label: 'sidebar.informations',
            permissions: ['INFO_CMP'],
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={fallback}>
                    <Informations />
                  </Suspense>
                ),
                label: 'sidebar.informations'
              },
              {
                path: 'details/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <InformationDetails />
                  </Suspense>
                ),
                label: 'details'
              }
            ]
          },
          {
            path: 'monthly-statement',
            label: 'monthlyStatementListing.title',
            permissions: ['RELEVE_MENU'],
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={fallback}>
                    <MonthlyStatementListing />
                  </Suspense>
                ),
                label: 'monthlyStatementListing.title'
              },
              {
                path: 'edit/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <MonthlyStatementDetails readOnly={false} />
                  </Suspense>
                ),
                permissions: ['RELEVE_MENU_REC_RES'],
                label: 'edit'
              },
              {
                path: 'view/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <MonthlyStatementDetails readOnly={true} />
                  </Suspense>
                ),
                label: 'Voir'
              }
            ]
          },
          {
            // B-Reader tours moved to /breader/tours (APPSUPPORT-2260)
            path: 'breader/tours/*',
            element: (
              <Suspense fallback={fallback}>
                <LegacyToursRedirect />
              </Suspense>
            )
          },
          {
            path: 'breader/*',
            element: <Navigate to="/breader/tours" replace />
          }
        ]
      },
      {
        path: 'breader',
        label: 'B-Reader',
        element: (
          <Suspense fallback={fallback}>
            <RouteRedirect baseRoute="/breader" targetRoute="/breader/tours" />
          </Suspense>
        ),
        children: [
          {
            path: 'tours',
            permissions: ['BREADER_MENU'],
            element: (
              <Suspense fallback={fallback}>
                <Tours />
              </Suspense>
            ),
            children: [
              {
                path: '',
                label: 'sidebar.tours',
                element: (
                  <Suspense fallback={fallback}>
                    <ToursList />
                  </Suspense>
                )
              },
              {
                path: 'add',
                label: 'add',
                element: (
                  <Suspense fallback={fallback}>
                    <TourForm />
                  </Suspense>
                )
              },
              {
                path: 'details/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <TourDetails />
                  </Suspense>
                ),
                label: 'details'
              }
            ]
          }
        ]
      },
      {
        path: 'boe',
        permissions: ['BOE'],
        label: 'Support',
        element: (
          <Suspense fallback={fallback}>
            <RouteRedirect baseRoute="/boe" targetRoute="/boe/cr/interventions" />
          </Suspense>
        ),
        children: [
          {
            path: 'cr',
            label: 'Concentrateurs',
            permissions: ['CR_MENU'],
            element: (
              <Suspense fallback={fallback}>
                <RouteRedirect baseRoute="/boe/cr" targetRoute="/boe/cr/interventions" />
              </Suspense>
            ),
            children: [
              {
                path: 'planning',
                element: (
                  <Suspense fallback={fallback}>
                    <Planning />
                  </Suspense>
                ),
                label: 'Planning'
              },
              {
                path: 'interventions',
                label: 'Interventions',
                children: [
                  {
                    path: '',
                    element: (
                      <Suspense fallback={fallback}>
                        <Interventions />
                      </Suspense>
                    ),
                    label: 'Liste'
                  },
                  {
                    path: 'details/:id',
                    element: (
                      <Suspense fallback={fallback}>
                        <InterventionForm />
                      </Suspense>
                    ),
                    label: 'details'
                  }
                ]
              }
            ]
          },
          {
            path: 'dms',
            element: (
              <Suspense fallback={fallback}>
                <DMSManager readOnly={true} />
              </Suspense>
            ),
            permissions: ['GED_MENU'],
            label: 'Gestion Electronique de Documents'
          },
          {
            path: 'informations',
            label: 'Informations',
            permissions: ['BOE'],
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={fallback}>
                    <Informations />
                  </Suspense>
                ),
                label: 'Informations complémentaires'
              },
              {
                path: 'details/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <InformationDetails />
                  </Suspense>
                ),
                label: 'details'
              }
            ]
          },
          {
            path: 'monthly-statement',
            label: 'monthlyStatementListing.title',
            element: (
              <Suspense fallback={fallback}>
                <Outlet />
              </Suspense>
            ),
            permissions: ['RELEVE_MENU'],
            children: [
              {
                path: '',
                element: (
                  <Suspense fallback={fallback}>
                    <MonthlyStatementListing />
                  </Suspense>
                ),
                label: 'monthlyStatementListing.title'
              },
              {
                path: 'add/:year/:month',
                element: (
                  <Suspense fallback={fallback}>
                    <SubcontractorMonthlyStatementDetails readOnly={false} />
                  </Suspense>
                ),
                permissions: ['RELEVE_MENU_CREATE'],
                label: 'add'
              },
              {
                path: 'edit/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <SubcontractorMonthlyStatementDetails readOnly={false} />
                  </Suspense>
                ),
                label: 'edit'
              },
              {
                path: 'view/:id',
                element: (
                  <Suspense fallback={fallback}>
                    <SubcontractorMonthlyStatementDetails readOnly={true} />
                  </Suspense>
                ),
                label: 'Voir'
              }
            ]
          },
          {
            path: 'administration',
            permissions: ['ADMIN_MENU'],
            label: 'administration',
            element: (
              <Suspense fallback={fallback}>
                <RouteRedirect
                  baseRoute="/boe/administration"
                  targetRoute="/boe/administration/subcontractors"
                />
              </Suspense>
            ),
            children: [
              {
                path: 'subcontractors',
                element: (
                  <Suspense fallback={fallback}>
                    <SubcontractorBOE />
                  </Suspense>
                ),
                label: 'Sous-traitant'
              }
            ]
          }
        ]
      }
    ]
  },
  {
    path: 'sample-page',
    element: (
      <Suspense fallback={fallback}>
        <DashboardLayout component={<SamplePage />} />
      </Suspense>
    )
  },
  {
    path: '*',
    element: (
      <Suspense fallback={fallback}>
        <DashboardLayout component={<NotFound />} />
      </Suspense>
    )
  }
];

export default routes;
